import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSessionStore } from '../store/sessionStore';
import { generateBlanksPDFV2 } from '../services/pdfServiceV2';
import { BlankCard } from './BlankCard';
import './BlanksGenerator.css';

/**
 * Компонент для генерации PDF бланков игры
 * Позволяет создать бланки с сеткой 5x5 для печати
 */
export const BlanksGenerator = () => {
  const currentPackage = useSessionStore((state) => state.session.package);
  const [isOpen, setIsOpen] = useState(false);
  const [count, setCount] = useState(10);
  const blankRefs = useRef<React.RefObject<HTMLDivElement>[]>([]);

  const handleGenerate = async () => {
    if (!currentPackage) {
      alert('Сначала выберите пакет игры');
      return;
    }

    if (count < 1 || count > 100) {
      alert('Количество бланков должно быть от 1 до 100');
      return;
    }

    setIsOpen(false);
    // Показываем индикатор загрузки
    const loadingMessage = document.createElement('div');
    loadingMessage.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px 40px;
      border-radius: 8px;
      z-index: 10002;
      font-size: 18px;
    `;
    loadingMessage.textContent = 'Создание PDF бланков...';
    document.body.appendChild(loadingMessage);

    try {
      // Создаем ref'ы для каждого бланка, если их еще нет
      if (blankRefs.current.length !== count) {
        blankRefs.current = Array.from({ length: count }, () => 
          React.createRef<HTMLDivElement>()
        );
      }
      
      // Ждем, чтобы React успел отрендерить бланки
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await generateBlanksPDFV2(blankRefs.current, currentPackage.name);
    } catch (error) {
      console.error('Ошибка создания бланков:', error);
      alert('Произошла ошибка при создании бланков');
    } finally {
      document.body.removeChild(loadingMessage);
    }
  };

  if (!currentPackage) {
    return null;
  }

  return (
    <>
      <button
        className="blanks-generator-button"
        onClick={() => setIsOpen(true)}
        title="Создать бланки для печати"
      >
        📄 Создать бланки
      </button>

      {/* Скрытые контейнеры с бланками для react-to-pdf (всегда рендерятся, но скрыты) */}
      {currentPackage && (
        <div style={{ 
          position: 'absolute', 
          left: '-9999px', 
          top: '0',
          clipPath: 'inset(0 0 0 100%)',
          pointerEvents: 'none'
        }}>
          {Array.from({ length: count }).map((_, index) => {
            // Создаем ref для каждого бланка, если его еще нет
            if (!blankRefs.current[index]) {
              blankRefs.current[index] = React.createRef<HTMLDivElement>();
            }
            
            return (
              <div key={index} ref={blankRefs.current[index]}>
                <BlankCard
                  tracks={currentPackage.tracks}
                  pageNumber={index + 1}
                />
              </div>
            );
          })}
        </div>
      )}

      {isOpen && createPortal(
        <div className="blanks-generator-overlay" onClick={() => setIsOpen(false)}>
          <div className="blanks-generator-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Создание бланков</h3>
            <p className="blanks-generator-info">
              Будет создан PDF файл с бланками для игры "Музыкальное Лото".
              <br />
              Каждый бланк содержит сетку 5×5 с случайным распределением песен из пакета "{currentPackage.name}".
            </p>
            <div className="blanks-generator-field">
              <label>
                Количество бланков:
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value, 10) || 1)}
                  className="blanks-generator-input"
                />
              </label>
            </div>
            <div className="blanks-generator-actions">
              <button onClick={handleGenerate} className="blanks-generator-generate">
                Создать PDF
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="blanks-generator-cancel"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

